import { useState, useEffect, useRef, useMemo } from "react";
import { useForm } from "react-hook-form";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { getRequestWithUnfinishedDispositionsTemplate } from "../../document-templates/RequestWithUnfinishedDispositionsTemplate";
import { DispositionsByUnfinishedRequest } from "../../forms/DispositionsByUnfinishedRequest/DispositionsByUnfinishedRequest";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { DateInput } from "../../components/DateInput/DateInput";
import { Request, RequestType } from "../../models/Request";
import { Utility } from "../../core/Utility";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { useApplicationContext } from "../../application/ApplicationContext";
import {
  getAllDocuments,
  getAllDispositionsByDate,
  getAllRequests,
} from "../../services/NewVersionService";
import "./RequestWithUnfinishedDispositionsPage.scss";
import { useTranslation } from "react-i18next";
import { AxiosResponse } from "axios";

export const RequestWithUnfinishedDispositionsPage = () => {
  const { t } = useTranslation();
  const { state } = useApplicationContext();

  const businessYear = state.businessYear ?? new Date().getFullYear();
  const yearStart = useMemo(
    () => new Date(businessYear, 0, 1, 0, 0, 1),
    [businessYear],
  );
  const yearEnd = useMemo(
    () => new Date(businessYear, 11, 31, 23, 59, 59),
    [businessYear],
  );
  const getDefaultRangeForBusinessYear = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    if (businessYear === currentYear) {
      const sevenDaysAgo = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() - 30,
        0,
        0,
        1,
      );
      const from = Utility.clampDateToRange(sevenDaysAgo, yearStart, yearEnd);
      const to = Utility.clampDateToRange(today, yearStart, yearEnd);
      return { from, to };
    }
    return { from: yearStart, to: yearEnd };
  };

  const [dateFrom, setDateFrom] = useState<Date>(
    () => getDefaultRangeForBusinessYear().from,
  );
  const [dateTo, setDateTo] = useState<Date>(
    () => getDefaultRangeForBusinessYear().to,
  );

  const [selectedPlace, setSelectedPlace] = useState<string>("");

  const [processing, setProcessing] = useState(false);
  const [allPlaces, setAllPlaces] = useState<string[]>([]);
  const [mercantileRequestData, setMercantileRequestData] = useState<Request[]>(
    [],
  );
  const [repromaterialRequestData, setRepromaterialRequestData] = useState<
    Request[]
  >([]);
  const [dispositionsVisible, setDispositionsVisible] =
    useState<boolean>(false);
  const [selectedRequestId, setSelectedRequestId] = useState<string>();
  const [pdfData, setPdfData] = useState<any>(
    getRequestWithUnfinishedDispositionsTemplate(
      mercantileRequestData,
      repromaterialRequestData,
      selectedPlace,
      dateFrom,
      dateTo,
    ),
  );
  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  useEffect(() => {
    const { from, to } = getDefaultRangeForBusinessYear();
    setDateFrom(from);
    setDateTo(to);
  }, [businessYear, yearStart, yearEnd]);

  useEffect(() => {
    const run = async () => {
      setProcessing(true);
      try {
        const toMs = new Date(dateTo).getTime() + 24 * 60 * 60 * 1000;

        // 1) povuci dispositions od dateFrom (bez dateto), pa preseci na dateTo
        const dispResp: AxiosResponse =
          await getAllDispositionsByDate(dateFrom);
        const dispositionsRaw = dispResp.data.result ?? [];
        const dispositionsInRange = dispositionsRaw.filter((d: any) => {
          const ts = new Date(
            d?.dateFrom ?? d?.date ?? d?.createdAt ?? dateFrom,
          ).getTime();
          return ts <= toMs;
        });

        // 2) fallback: ako dispositions nemaju request, povuci requeste iz opsega i napravi index
        let requestById = new Map<string, Request>();
        const needRequestsFallback = dispositionsInRange.some(
          (d: any) => !d?.request && d?.requestId,
        );

        if (needRequestsFallback) {
          const [mercReqResp, reproReqResp]: [AxiosResponse, AxiosResponse] =
            await Promise.all([
              getAllRequests(true, dateFrom, dateTo),
              getAllRequests(false, dateFrom, dateTo),
            ]);

          const allReq: Request[] = [
            ...(mercReqResp.data.result ?? []),
            ...(reproReqResp.data.result ?? []),
          ];

          requestById = new Map(
            allReq.filter((r) => r?.id).map((r) => [r.id!.toString(), r]),
          );
        }

        // 3) index: dispositionId -> request
        const dispToReq = new Map<string, Request>();
        for (const d of dispositionsInRange) {
          const dispId = d?.id;
          if (!dispId) continue;

          const req: Request | undefined =
            (d?.request as Request) ||
            (d?.requestId
              ? requestById.get(d.requestId.toString())
              : undefined);

          if (req) dispToReq.set(dispId.toString(), req);
        }

        // 4) povuci dokumente za filter opseg
        const [miResp, moResp, riResp, roResp]: [
          AxiosResponse,
          AxiosResponse,
          AxiosResponse,
          AxiosResponse,
        ] = await Promise.all([
          getAllDocuments(true, true, dateFrom, dateTo),
          getAllDocuments(true, false, dateFrom, dateTo),
          getAllDocuments(false, true, dateFrom, dateTo),
          getAllDocuments(false, false, dateFrom, dateTo),
        ]);

        const mercInputs = miResp.data.result ?? [];
        const mercOutputs = moResp.data.result ?? [];
        const reproInputs = riResp.data.result ?? [];
        const reproOutputs = roResp.data.result ?? [];

        const placeOk = (req: Request) =>
          selectedPlace === "" || req.place === selectedPlace;

        const buildRequests = (docs: any[], unfinishedOnly: boolean) => {
          const out: Request[] = [];
          const seen = new Set<string>();

          for (const doc of docs ?? []) {
            if (unfinishedOnly && doc?.finishedDocument === true) continue;

            const dispId = doc?.dispositionId;
            if (!dispId) continue;

            const req = dispToReq.get(dispId.toString());
            if (!req) continue;
            if (!placeOk(req)) continue;

            const key = (req.id ?? "").toString();
            if (!key || seen.has(key)) continue;

            seen.add(key);
            out.push(req);
          }

          out.sort(
            (a, b) =>
              new Date(b.date as Date).getTime() -
              new Date(a.date as Date).getTime(),
          );

          return out;
        };

        setMercantileRequestData(
          buildRequests([...mercInputs, ...mercOutputs], true),
        );

        setRepromaterialRequestData(
          buildRequests([...reproInputs, ...reproOutputs], false),
        );

        // 5) mesta (filter dropdown) — možemo dobiti iz requests iz dispositions
        const places = Array.from(
          new Set(
            dispositionsInRange
              .map((d: any) => (d?.request as Request)?.place)
              .filter(Boolean) as string[],
          ),
        ).sort((a, b) => a.localeCompare(b, "sr"));

        setAllPlaces(places);
      } finally {
        setProcessing(false);
      }
    };

    run();
  }, [dateFrom, dateTo, selectedPlace]);

  // populate pdf when data changes
  useEffect(() => {
    const newPdf = getRequestWithUnfinishedDispositionsTemplate(
      mercantileRequestData,
      repromaterialRequestData,
      selectedPlace,
      dateFrom,
      dateTo,
    );
    setPdfData(newPdf);
  }, [mercantileRequestData, repromaterialRequestData]);

  const onSelectedPlaceChange = (property: string, value: string) => {
    setSelectedPlace(value);
  };

  const onDateFromChange = (_property: string, value: Date | null) => {
    if (!value) {
      setDateFrom(yearStart);
      return;
    }
    let d = Utility.clampDateToRange(value, yearStart, yearEnd);
    if (d > dateTo) {
      d = dateTo;
    }
    setDateFrom(d);
  };

  const onDateToChange = (_property: string, value: Date | null) => {
    if (!value) {
      setDateTo(yearEnd);
      return;
    }
    let d = Utility.clampDateToRange(value, yearStart, yearEnd);
    if (d < dateFrom) {
      d = dateFrom;
    }
    setDateTo(d);
  };

  const renderMercantileHeader = () => {
    return (
      <div className="repromaterial-header">
        <span>{t("MERKANTILA")}</span>
      </div>
    );
  };

  const renderRepromaterialHeader = () => {
    return (
      <div className="repromaterial-header">
        <span>{t("REPROMATERIJAL")}</span>
      </div>
    );
  };

  const formatType = (rowData: any) => {
    return rowData.type === RequestType.Import
      ? "Prijem"
      : rowData.type === RequestType.Export
        ? "Prodaja"
        : "Premeštaj";
  };

  const formatDate = (rowData: any) => {
    return Utility.formatDate(new Date(rowData.date as Date));
  };

  const formatPartner = (rowData: any) => {
    return rowData.partner?.name;
  };

  const formatProduct = (rowData: any) => {
    return rowData.product?.name;
  };

  const formatPlace = (rowData: any) => {
    return rowData.place;
  };

  const formatAmount = (rowData: any) => {
    return (
      rowData.amount?.toString() +
      " " +
      rowData.product?.measuringUnit.toLowerCase()
    );
  };

  const exportExcel = () => {
    const excelMercantileObject = mercantileRequestData.map((item: Request) => {
      return {
        Tip:
          item.type === RequestType.Import
            ? "Prijem"
            : item.type === RequestType.Export
              ? "Prodaja"
              : "Premeštaj",
        Datum: Utility.formatDate(new Date(item.date as Date)),
        Broj: item.requestNumberFormatted,
        Partner: item.partner?.name,
        Roba: item.product?.name,
        Mesto: item.place,
        Količina:
          item.amount?.toString() +
          " " +
          item.product?.measuringUnit?.toLocaleLowerCase(),
      };
    });
    const excelRepromaterialObject = repromaterialRequestData.map(
      (item: Request) => {
        return {
          Tip:
            item.type === RequestType.Import
              ? "Prijem"
              : item.type === RequestType.Export
                ? "Prodaja"
                : "Premeštaj",
          Datum: Utility.formatDate(new Date(item.date as Date)),
          Broj: item.requestNumberFormatted,
          Partner: item.partner?.name,
          Roba: " ",
          Mesto: item.place,
          Količina: " ",
        };
      },
    );
    const excelObject = [
      {
        Tip: "MERKANTILA",
        Datum: "",
        Broj: "",
        Partner: "",
        Roba: "",
        Mesto: "",
        Količina: "",
      },
      ...excelMercantileObject,
      {
        Tip: "REPROMATERIJAL",
        Datum: "",
        Broj: "",
        Partner: "",
        Roba: "",
        Mesto: "",
        Količina: "",
      },
      ...excelRepromaterialObject,
    ];
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(excelObject);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAsExcelFile(excelBuffer, "Zahtevi sa nerealizovanim dispozicijama");
    });
  };

  const saveAsExcelFile = (buffer: any, fileName: any) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(data, fileName + EXCEL_EXTENSION);
      }
    });
  };

  const {
    control,
    formState: { errors },
  } = useForm({
    mode: "all",
  });

  const viewActionBodyTemplate = (rowData: any) => {
    return (
      <ButtonInput
        addStyle={{ border: "none", background: "#198754" }}
        labelText={""}
        icon="bi bi-eye"
        onButtonClick={() => {
          if (rowData?.id) {
            setSelectedRequestId(rowData.id);
            setDispositionsVisible(true);
          }
        }}
      />
    );
  };

  return (
    <PageLayout>
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-file-text header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
                transform: "translate(0, 5px)",
              }}
            ></i>
            <span className="header-title">
              {t("Zahtevi koji imaju nerealizovane dispozicije kreirane od")}
            </span>
            <DateInput
              id="dateFrom"
              value={dateFrom}
              onValueChange={onDateFromChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements">{t("do")}</span>
            <DateInput
              id="dateTo"
              value={dateTo}
              onValueChange={onDateToChange}
              minDate={yearStart}
              maxDate={yearEnd}
              labelText=""
              placeholderText=""
              icon="bi bi-calendar-date"
              control={control}
              errors={errors}
              disabled={processing}
            />
            <span className="header-elements">{t("za")}</span>
            <DropdownInput
              id="selectedPartner"
              value={selectedPlace}
              onValueChange={onSelectedPlaceChange}
              options={allPlaces}
              filter={false}
              labelText=""
              placeholderText={t("Mesto")}
              control={control}
              errors={errors}
              disabled={processing}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-file-earmark-excel"
              onButtonClick={exportExcel}
              disabled={processing}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                margin: "0 .5rem",
              }}
              labelText={""}
              icon="bi bi-file-earmark-pdf"
              onButtonClick={() => {
                if (downloadPdfDocument.current) {
                  // @ts-ignore
                  downloadPdfDocument.current();
                }
              }}
              disabled={processing}
            />
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
              }}
              labelText={""}
              icon="bi bi-printer"
              onButtonClick={() => {
                if (printPdfDocument.current) {
                  // @ts-ignore
                  printPdfDocument.current();
                }
              }}
              disabled={processing}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              header={renderMercantileHeader}
              value={mercantileRequestData}
              showGridlines
              stripedRows
            >
              <Column body={formatType} header={t("Tip")} />
              <Column body={formatDate} header={t("Datum zahteva")} />
              <Column
                field="requestNumberFormatted"
                header={t("Broj zahteva")}
              />
              <Column body={formatPartner} header={t("Partner")} />
              <Column body={formatProduct} header={t("Roba")} />
              <Column body={formatPlace} header={t("Mesto")} />
              <Column body={formatAmount} header={t("Količina")} />
              <Column
                body={viewActionBodyTemplate}
                className="datatable-button-column"
                header={t("Akcija")}
              />
            </DataTable>
          </div>
        )}
        {!processing && (
          <div className="table-body">
            <DataTable
              header={renderRepromaterialHeader}
              value={repromaterialRequestData}
              showGridlines
              stripedRows
            >
              <Column body={formatType} header={t("Tip")} />
              <Column body={formatDate} header={t("Datum zahteva")} />
              <Column
                field="requestNumberFormatted"
                header={t("Broj zahteva")}
              />
              <Column body={formatPartner} header={t("Partner")} />
              <Column body={formatPlace} header={t("Mesto")} />
              <Column
                body={viewActionBodyTemplate}
                className="datatable-button-column"
                header={t("Akcija")}
              />
            </DataTable>
          </div>
        )}
        <DispositionsByUnfinishedRequest
          visible={dispositionsVisible}
          requestId={selectedRequestId as string}
          buttonInactiveStyle={{
            border: "none",
            background: "rgba(108, 117, 125, 1)",
          }}
          handleClose={() => setDispositionsVisible(false)}
        />
        <div className="document-preview">
          <PdfPreview
            pdfDefinition={pdfData}
            downloadDocument={downloadPdfDocument}
            printDocument={printPdfDocument}
            documentName={`${t(
              "Zahtevi sa nerealizovanim dispozicijama ",
            )} ${Utility.formatDate(new Date())}.pdf`}
          />
        </div>
      </div>
    </PageLayout>
  );
};
